import { Button, Card, HSpacer, Text, VSpacer } from "@/components/DesignSystem";
import { Avatar } from "@/components/DesignSystem/Avatar/Avatar";
import { LoyaltySkuBadge } from '@/components/shared/LoyaltySkuBadge';
import { PointsTag } from "@/components/shared/PointsTag";
import { AppConfig } from "@/constants/AppConfig";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  ApiProduct,
  ApiSubwalletBalance,
  ResolvedApiSubwallet,
  ResolvedApiSubwalletBalance,
} from '@api/interfaces';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Stack, useTheme } from "@mui/material";
import {
  calculateUnexpiredPoints,
  groupByDaysUntilExpiration,
} from "@shared/utilities";
import React, { Fragment } from "react";
import { Trans, useTranslation } from 'react-i18next';
import { MaterialSymbol } from "react-material-symbols";

interface SubwalletCardProps {
  balances: ResolvedApiSubwalletBalance[],
  hideBorder?: boolean,
  lockedUntilDate?: string | null,
  loyaltySkus: ApiProduct[],
  onClick: () => void;
  title?: string,
}

export const SubwalletCard = ({
  balances,
  hideBorder,
  lockedUntilDate,
  loyaltySkus,
  onClick,
  title,
}: SubwalletCardProps) => {
  const theme = useTheme();
  const { isMobile } = useMediaQuery();
  const { t } = useTranslation();

  const PointsExpiringSoonLineItem = ({
    days,
    points,
  }: {
    days: number,
    points: number,
  }) => {
    if (!points) {
      return null;
    }
    let status: 'error' | 'warning' | 'info' = 'error';
    if (days >= 30) {
      status = 'warning';
    }
    if (days >= 60) {
      status = 'info';
    }
    return (
      <Stack alignItems="center" direction="row">
        <HSpacer size="3"/>
        <HSpacer size="2"/>
        <MaterialSymbol
          color={theme.palette[status].main}
          fill
          icon="alarm"
        />
        <HSpacer size="2"/>
        <Text category="body-medium">
          <Trans
            components={{
              bold: <b />,
              expiringText: (
                <>
                  {t(
                    $ => $.customerDashboard.pointsExpiration.dialog.expiringInDays,
                    { count: days },
                  )}
                </>
              ),
            }}
            count={points}
            i18nKey={$ => $.customerDashboard.pointsExpiration.dialog.pointsExpiringText}
            values={{ points }}
          />
        </Text>
      </Stack>
    );
  };

  const walletsGroupedByExpiration = groupByDaysUntilExpiration<ApiSubwalletBalance>(balances);

  const getLogo = (subwallet: ResolvedApiSubwallet) => {
    return subwallet.manufacturer?.logo || subwallet.retailer.logomark;
  };

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: hideBorder ? 'none !important' : undefined,
        boxShadow:
          hideBorder ?
            'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
        cursor: 'pointer',
      }}
      testID={`${title ?? balances[0].subwallet.name}-wallet-card`}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack direction="row">
          <Avatar
            alt={`${balances[0].subwallet.name} logo`}
            defaultIcon={balances[0].manufacturerId ? 'manufacturer' : 'retailer'}
            size={48}
            src={balances[0].subwallet && getLogo(balances[0].subwallet)
              ? `${AppConfig.staticImageHost}/${getLogo(balances[0].subwallet)}`
              : undefined
            }
          />
          <HSpacer size="5"/>
          <Stack>
            <Stack alignItems="center" direction="row">
              <Text category="body-xlarge">
                {title ?? balances[0].subwallet.name}
              </Text>
              {!!lockedUntilDate && (
                <>
                  <HSpacer size="2"/>
                  <MaterialSymbol
                    color={theme.palette.grey[700]}
                    fill
                    icon="lock"
                  />
                </>
              )}
            </Stack>
            <VSpacer size="1"/>
            <VSpacer size="2"/>
            <PointsTag
              points={calculateUnexpiredPoints(balances)}
              textCategory="label-medium"
              variant="filled"
            />
          </Stack>
        </Stack>
        {isMobile ? (
          <KeyboardArrowDownIcon />
        ) : (
          <Button
            color="inherit"
            sx={{ borderColor: 'rgba(0, 0, 0, 0.16)', marginLeft: '12px' }}
            testID="subwallet-card-details-button"
            variant="outlined"
          >
            {t($ => $.common.actions.details)}
          </Button>
        )}
      </Stack>
      {!!lockedUntilDate && (
        <>
          <VSpacer size="3"/>
          <Stack alignItems="center" direction="row">
            <HSpacer size="3"/>
            <HSpacer size="2"/>
            <Text category="body-medium" sx={{ color: theme.palette.grey[700] }}>
              {t($ => $.wallet.locked.lockedUntil, {
                date: new Date(`${lockedUntilDate}T00:00:00`),
              })}
            </Text>
          </Stack>
          <VSpacer size="2"/>
        </>
      )}
      {Object.keys(walletsGroupedByExpiration).map((id) => (
        <Fragment key={id}>
          <VSpacer size="3"/>
          <PointsExpiringSoonLineItem
            days={Number(id)}
            points={walletsGroupedByExpiration[id].reduce((accumulator, currentWallet) => {
              return accumulator + currentWallet.points;
            }, 0)}
          />
          <VSpacer size="2"/>
        </Fragment>
      ))}
      <Stack direction="row" flexWrap="wrap">
        {loyaltySkus.map((sku) => (
          <Fragment key={sku.externalId}>
            <Stack>
              <VSpacer size="5"/>
              <LoyaltySkuBadge sku={sku.externalId!} />
            </Stack>
            <HSpacer size="4"/>
          </Fragment>
        ))}
      </Stack>
    </Card>
  );
};
